import {ActionTypes} from '../reducer/actions';
import {useEmailCommonHandlers} from './useEmailCommonHandlers';
import {LexicalEditor} from 'lexical';
import {getHtmlContent} from '../../../utils/lexical/getHtmlContent';
import {insertUrlContent} from '../utils/insertUrlContent';
import {insertIdTreeContent} from '../utils/insertIdTreeContent';
import {TUseEmailContextAPI} from './types';
import {TPersonalizeStates} from '../components/SideBar/parts/Personalize/hooks/types';
import {THyperlinkStates} from '../components/SideBar/parts/Hyperlink/hooks/types';
import {useCallback} from 'react';
import {replaceTitlesWithIds, replaceTitlesWithIdsForHtml} from '../utils/treeActions';
import {TEditorTemplatesDictionary, TEmailStateType, TEmailTemplateState} from '../reducer/types';

export const useEmailContextAPI = ({dispatch, contextValue, onSave}: TUseEmailContextAPI) => {
    const {onBack} = useEmailCommonHandlers({dispatch, isDirty: contextValue.states.isDirty});

    const updatePersonalize = async (personalizeState: TPersonalizeStates) => {
        await insertIdTreeContent(
            contextValue.states.emailEditor,
            personalizeState.tree?.extraData.formTitle || '',
            personalizeState.tree?.title || ''
        );

        dispatch({type: ActionTypes.PERSONALIZE_UPDATE, payload: {personalizeState}});
    };

    const updateHyperlinkButton = async (hyperlinkStates: THyperlinkStates) => {
        await insertUrlContent(contextValue.states.emailEditor, hyperlinkStates);
        dispatch({
            type: ActionTypes.HYPERLINK_STATES_UPDATE,
            payload: {hyperlinkStates: {text: '', url: '', active: true}}
        });
    };

    const updateHyperlinkStates = async (hyperlinkStates: THyperlinkStates) => {
        dispatch({type: ActionTypes.HYPERLINK_STATES_UPDATE, payload: {hyperlinkStates}});
    };

    const updateActiveHyperlink = async (isActive: boolean) => {
        dispatch({type: ActionTypes.HYPERLINK_ACTIVE_UPDATE, payload: {isActive}});
        if (!isActive) {
            dispatch({
                type: ActionTypes.HYPERLINK_STATES_UPDATE,
                payload: {hyperlinkStates: {text: '', url: '', active: false}}
            });
        }
    };

    const updateImage = (imageState: string) => {
        dispatch({type: ActionTypes.IMAGE_UPDATE, payload: {imageState}});
    };

    const updateLanguage = (language: string) => {
        dispatch({type: ActionTypes.LANGUAGE_UPDATE, payload: {language}});
    };

    const updateEmailEditor = (editor: LexicalEditor) => {
        dispatch({type: ActionTypes.UPDATE_EMAIL_EDITOR, payload: {editor}});
    };

    const updateEmailHTML = async () => {
        const emailHtml = await getHtmlContent(
            contextValue.states.emailEditor,
            null,
            contextValue.states.language === '1'
        );

        dispatch({type: ActionTypes.UPDATE_EMAIL_HTML, payload: {emailHtml}});
        return {emailHtml, language: contextValue.states.language};
    };

    const updateEmailSubject = (subject: string) => {
        dispatch({type: ActionTypes.UPDATE_EMAIL_SUBJECT, payload: {subject}});
    };

    const saveEmailEditor = async (
        {htmlString, language, treeValues},
        cb?: (updatedEditorState: TEmailStateType) => void
    ) => {
        const templates: TEditorTemplatesDictionary = Object.keys(
            contextValue.states.templates || {}
        ).reduce((acc, langId) => {
            const currentTemplate = contextValue.states.templates[langId] as TEmailTemplateState;
            const html = language === langId ? htmlString : currentTemplate.emailHtml;

            acc[langId] = {
                ...currentTemplate,
                editorSubjectIds: replaceTitlesWithIds(
                    currentTemplate.editorSubject || '',
                    treeValues
                ),
                emailHtml: replaceTitlesWithIdsForHtml(html || '', treeValues)
            };
            return acc;
        }, {});

        if (onSave) {
            onSave(templates);
        }
    };

    const setIsDirty = useCallback(
        (isDirty: boolean) => {
            dispatch({type: ActionTypes.UPDATE_DIRTY_STATE, payload: {isDirty}});
        },
        [dispatch]
    );

    const toggleAILoading = useCallback(
        (aiLoading: boolean) => {
            dispatch({type: ActionTypes.UPDATE_AI_LOADING_STATE, payload: {aiLoading}});
        },
        [dispatch]
    );

    return {
        onBack,
        updatePersonalize,
        updateHyperlinkButton,
        updateHyperlinkStates,
        updateActiveHyperlink,
        updateImage,
        updateLanguage,
        updateEmailEditor,
        updateEmailHTML,
        updateEmailSubject,
        saveEmailEditor,
        toggleAILoading,
        setIsDirty
    } as const;
};
